import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { User, FileText, Glasses, Ruler, CreditCard, CheckCircle } from 'lucide-react';

const ProgressContainer = styled.div`
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.35rem 1rem 0.5rem;
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.75rem 0.45rem;
    overflow-x: auto;
  }
`;

const ProgressSteps = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 13px;
    left: 0;
    right: 0;
    height: 1px;
    background: #e2e8f0;
    z-index: 0;
    
    @media (max-width: 768px) {
      top: 11px;
    }
  }
  
  @media (max-width: 768px) {
    min-width: 520px;
    padding: 0 0.5rem;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  z-index: 1;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  min-width: 0;
  
  &:hover {
    ${props => props.clickable && `
      .step-icon {
        opacity: 0.92;
      }
    `}
  }
`;

const StepIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.completed) return '#10b981';
    if (props.active) return '#667eea';
    return '#f1f5f9';
  }};
  color: ${props => {
    if (props.completed || props.active) return 'white';
    return '#94a3b8';
  }};
  margin-bottom: 0.2rem;
  transition: opacity 0.2s ease, background 0.2s ease, color 0.2s ease;
  box-shadow: none;
  flex-shrink: 0;
  position: relative;
  border: 1px solid ${props => (props.active || props.completed ? 'transparent' : '#e2e8f0')};
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
  }
`;

const StepLabel = styled.span`
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: ${props => {
    if (props.completed) return '#059669';
    if (props.active) return '#5b67d9';
    return '#94a3b8';
  }};
  text-align: center;
  transition: color 0.2s ease;
  white-space: nowrap;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 0.55rem;
  }
`;

const ConnectingLine = styled.div`
  position: absolute;
  top: 13px;
  left: ${props => props.start}%;
  width: ${props => props.width}%;
  height: 1px;
  background: ${props => props.completed ? '#10b981' : '#e2e8f0'};
  z-index: 0;
  transition: background 0.2s ease;
  
  @media (max-width: 768px) {
    top: 11px;
  }
`;

const steps = [
  { id: 'visitor-entry', path: '/visitor-entry', labelKey: 'steps.visit', icon: User },
  { id: 'prescription', path: '/prescription', labelKey: 'steps.rx', icon: FileText },
  { id: 'frame-selection', path: '/frame-selection', labelKey: 'steps.frameSelection', icon: Glasses },
  { id: 'measurement', path: '/measurement', labelKey: 'steps.measures', icon: Ruler },
  { id: 'payment', path: '/payment', labelKey: 'steps.payment', icon: CreditCard },
  { id: 'order-confirmation', path: '/order-confirmation', labelKey: 'steps.confirmation', icon: CheckCircle }
];

const hasSessionValue = (key) => {
  const value = sessionStorage.getItem(key);
  return value !== null && value !== '' && value !== 'null' && value !== '{}';
};

const getFurthestSavedStepIndex = () => {
  if (hasSessionValue('paymentData')) return 5;
  if (hasSessionValue('measurementData') || hasSessionValue('paymentFormData')) return 4;
  if (hasSessionValue('selectedFrame') || hasSessionValue('frameSelectionData')) return 3;
  if (hasSessionValue('prescriptionId')) return 2;
  if (hasSessionValue('visitorId') || hasSessionValue('visitorData') || hasSessionValue('visitorFormData')) return 1;
  return 0;
};

const SalesProgressBar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine current step index
  const getCurrentStepIndex = () => {
    const currentPath = location.pathname;
    
    // Exclude sub-routes that shouldn't show progress bar
    const excludedPaths = [
      '/visitor-entry/search',
      '/visitor-entry/incomplete',
      '/visitor-entry/track'
    ];
    
    if (excludedPaths.some(path => currentPath.startsWith(path))) {
      return -1;
    }
    
    // Check for exact matches first
    for (let i = 0; i < steps.length; i++) {
      const stepPath = steps[i].path;
      // Handle both /prescription and /prescription-form
      if (stepPath === '/prescription') {
        if (currentPath === '/prescription' || currentPath === '/prescription-form' || 
            currentPath.startsWith('/prescription/') || currentPath.startsWith('/prescription-form/')) {
          return i;
        }
      } else if (currentPath === stepPath || (currentPath.startsWith(stepPath + '/') && stepPath !== '/visitor-entry')) {
        return i;
      } else if (stepPath === '/visitor-entry' && currentPath === '/visitor-entry') {
        // Only match exact /visitor-entry, not sub-routes
        return i;
      }
    }
    
    // Special cases
    if (currentPath === '/order-complete') {
      return steps.length; // Beyond all steps
    }
    
    // Default to first step if not in sales flow
    return -1;
  };

  const currentStepIndex = getCurrentStepIndex();
  const furthestSavedStepIndex = getFurthestSavedStepIndex();
  const furthestAvailableStepIndex = Math.max(currentStepIndex, furthestSavedStepIndex);

  // Determine step status
  const getStepStatus = (stepIndex) => {
    if (stepIndex === currentStepIndex) return 'active';
    if (stepIndex < currentStepIndex || stepIndex < furthestAvailableStepIndex) return 'completed';
    return 'pending';
  };

  // Check if we're in the sales flow
  const isInSalesFlow = currentStepIndex >= 0;

  // Don't show progress bar if not in sales flow
  if (!isInSalesFlow) {
    return null;
  }

  const handleStepClick = (step) => {
    // Allow navigation through the steps already reached in this visit.
    const stepIndex = steps.findIndex(s => s.id === step.id);
    if (stepIndex <= furthestAvailableStepIndex) {
      navigate(step.path);
    }
  };

  return (
    <ProgressContainer>
      <ProgressSteps>
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const Icon = step.icon;
          const isCompleted = status === 'completed';
          const isActive = status === 'active';
          const clickable = index <= furthestAvailableStepIndex;

          // Calculate connecting line for previous step
          const prevStepCompleted = index > 0 && getStepStatus(index - 1) === 'completed';
          
          return (
            <React.Fragment key={step.id}>
              {index > 0 && (
                <ConnectingLine
                  start={(index - 1) * (100 / (steps.length - 1))}
                  width={100 / (steps.length - 1)}
                  completed={prevStepCompleted || isCompleted}
                />
              )}
              <Step
                clickable={clickable}
                onClick={() => clickable && handleStepClick(step)}
                title={t(step.labelKey)}
              >
                <StepIcon
                  className="step-icon"
                  completed={isCompleted}
                  active={isActive}
                >
                  {isCompleted ? (
                    <CheckCircle size={14} strokeWidth={2.25} />
                  ) : (
                    <Icon size={14} strokeWidth={2} />
                  )}
                </StepIcon>
                <StepLabel completed={isCompleted} active={isActive}>
                  {t(step.labelKey)}
                </StepLabel>
              </Step>
            </React.Fragment>
          );
        })}
      </ProgressSteps>
    </ProgressContainer>
  );
};

export default SalesProgressBar;
